import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ExternalLink, Plus, X, StickyNote, Trash2, ShieldCheck, AlertTriangle, Clock, Ban,
  CircleDashed, CheckCircle2, Loader2, Paperclip, FileText, ListChecks, PoundSterling,
  Users, ClipboardList, CalendarDays, MapPin, Lightbulb, Bell, Pencil, ChevronRight,
  Circle, LayoutGrid, Activity as ActivityIcon, Link2,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useIsMobile } from "../../hooks/useViewport";
import AttachSheet from "./AttachSheet";

const READY_META = {
  ready: { label: "Ready", Icon: ShieldCheck, color: "#0F766E", bg: "rgba(15,118,110,0.1)" },
  needs_attention: { label: "Needs attention", Icon: AlertTriangle, color: "#B45309", bg: "rgba(180,83,9,0.1)" },
  waiting: { label: "Waiting", Icon: Clock, color: "#1D4ED8", bg: "rgba(29,78,216,0.08)" },
  blocked: { label: "Blocked", Icon: Ban, color: "#B91C1C", bg: "rgba(185,28,28,0.08)" },
  completed: { label: "Completed", Icon: CheckCircle2, color: "#0F766E", bg: "rgba(15,118,110,0.1)" },
  not_assessed: { label: "Not assessed", Icon: CircleDashed, color: "#9aa3a0", bg: "rgba(154,163,160,0.12)" },
};

const HUB_LABELS = {
  home_life: "Home Life", reminders: "Reminders", vault: "Vault", budget: "Budget",
  travel: "Travel", responsibilities: "Hubs", household: "Household", calendar: "Calendar",
};

const NAV_TABS = [
  { key: "overview", label: "Overview", Icon: LayoutGrid },
  { key: "preparation", label: "Preparation", Icon: CheckCircle2 },
  { key: "items", label: "Related items", Icon: Link2 },
  { key: "budget", label: "Budget", Icon: PoundSterling },
  { key: "people", label: "People", Icon: Users },
  { key: "activity", label: "Activity", Icon: ActivityIcon },
];

const CONNECT_META = {
  documents: { label: "Documents", Icon: FileText, tab: "items" },
  lists: { label: "Lists", Icon: ListChecks, tab: "items" },
  budget: { label: "Budget", Icon: PoundSterling, tab: "budget" },
  preparation: { label: "Tasks", Icon: CheckCircle2, tab: "preparation" },
  people: { label: "Contacts", Icon: Users, tab: "people" },
  reminders: { label: "Reminders", Icon: Bell, tab: "items" },
  related_records: { label: "Hub records", Icon: ClipboardList, tab: "items" },
};

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};
const fmtTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime()) || (!d.getHours() && !d.getMinutes())) return null;
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

function ReadinessPill({ ready }) {
  const rm = READY_META[ready?.state] || READY_META.not_assessed;
  const score = ready?.score;
  return (
    <span data-testid="ws-readiness" className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={{ color: rm.color, background: rm.bg }}>
      <rm.Icon className="h-3.5 w-3.5" />
      {score != null && ready.state !== "not_assessed" ? `${score}% ${ready.state === "ready" || ready.state === "completed" ? "Ready" : rm.label}` : rm.label}
    </span>
  );
}

function ReadinessRing({ ready }) {
  const rm = READY_META[ready?.state] || READY_META.not_assessed;
  const score = ready?.score ?? 0;
  const r = 30, c = 2 * Math.PI * r;
  return (
    <div className="relative h-20 w-20 shrink-0">
      <svg viewBox="0 0 72 72" className="h-20 w-20 -rotate-90">
        <circle cx="36" cy="36" r={r} fill="none" stroke="#EBECE8" strokeWidth="6" />
        <circle cx="36" cy="36" r={r} fill="none" stroke={rm.color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * score) / 100} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-base font-bold" style={{ color: rm.color }}>
        {ready?.score != null ? `${score}%` : "\u2013"}
      </span>
    </div>
  );
}

const TYPE_ICONS = {
  document: FileText, shopping_list: ListChecks, budget: PoundSterling, budget_txn: PoundSterling,
  contact: Users, responsibility: ClipboardList, trip: ClipboardList, home_life_item: CheckCircle2,
  reminder: Bell, need: ListChecks, scheduled_object: CalendarDays,
};

function LinkedCard({ entry, onRemoved, onOpen }) {
  const other = entry.other;
  const note = entry.link.metadata?.note;
  const Icon = TYPE_ICONS[other.source_type] || FileText;
  const remove = () => {
    if (!window.confirm("Remove this link? Both records stay exactly where they are.")) return;
    api.delete(`/record-links/${entry.link.id}`)
      .then(() => { toast.success("Link removed"); onRemoved(); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[#EBECE8] bg-white px-3.5 py-2.5"
      data-testid={`ws-link-${entry.link.id}`}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F4F6F2] text-[#0F766E]">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-[#2D3436]">{other.title}</span>
        <span className="block truncate text-[11px] text-[#9aa3a0]">
          {[HUB_LABELS[other.hub] || other.hub, entry.relationship_label,
            entry.link.required_for_readiness ? "Required" : null,
            (other.status || "").replace(/_/g, " ")].filter(Boolean).join(" \u00b7 ")}
        </span>
        {note && <span className="block truncate text-[11px] italic text-[#636E72]">{note}</span>}
      </span>
      {other.deep_link && (
        <button data-testid={`ws-link-open-${entry.link.id}`} title="Open source record" onClick={() => onOpen(other.deep_link)}
          className="rounded-lg border border-[#E2E8F0] px-2.5 py-1 text-[11px] font-medium text-[#636E72] hover:border-[#0F766E]/40 hover:text-[#0F766E]">
          Open
        </button>
      )}
      <button data-testid={`ws-link-remove-${entry.link.id}`} title="Remove link" onClick={remove}>
        <X className="h-3.5 w-3.5 text-[#CBD5E1] hover:text-[#B91C1C]" />
      </button>
    </div>
  );
}

// Related-items category card (mockup: RELATED ITEMS row).
function CategoryCard({ meta, entries, planned, onAttach, onCreate, createLabel, onOpen }) {
  return (
    <div className="flex flex-col rounded-2xl border border-[#EBECE8] bg-white p-4">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#F4F6F2] text-[#0F766E]">
          <meta.Icon className="h-4 w-4" />
        </span>
        <span>
          <span className="block text-sm font-semibold text-[#2D3436]">{meta.label}</span>
          <span className="block text-[11px] text-[#9aa3a0]">
            {planned != null ? `\u00a3${planned.toFixed(0)} planned` : `${entries.length} item${entries.length !== 1 ? "s" : ""}`}
          </span>
        </span>
      </div>
      <div className="mt-3 flex-1 space-y-1.5">
        {entries.slice(0, 2).map((e) => (
          <button key={e.link.id} onClick={() => e.other.deep_link && onOpen(e.other.deep_link)}
            className="flex w-full items-center gap-2 rounded-xl bg-[#FAFBF9] px-2.5 py-1.5 text-left">
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-[#2D3436]">{e.other.title}</span>
              <span className="block truncate text-[10px] text-[#9aa3a0]">{e.other.subtitle}</span>
            </span>
            <ChevronRight className="h-3 w-3 shrink-0 text-[#CBD5E1]" />
          </button>
        ))}
        {entries.length === 0 && <p className="py-2 text-[11px] text-[#9aa3a0]">Nothing here yet.</p>}
      </div>
      <div className="mt-3 flex gap-3 border-t border-[#F4F6F2] pt-2.5 text-[11px] font-medium">
        <button onClick={onAttach} className="text-[#0F766E] hover:underline">Attach existing</button>
        {onCreate && <button onClick={onCreate} className="text-[#636E72] hover:text-[#0F766E] hover:underline">{createLabel}</button>}
      </div>
    </div>
  );
}

// Editable readiness checklist + deterministic templates.
function ReadinessChecklist({ anchor, ev, ready, onChanged }) {
  const [templates, setTemplates] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [newCheck, setNewCheck] = useState("");
  const checks = ready?.checks || [];
  const finished = ready?.state === "completed";
  const openPicker = () => {
    if (!templates) {
      api.get(`/readiness/templates?hub=${ev.hub || ""}`).then((r) => setTemplates(r.data.templates || [])).catch(() => setTemplates([]));
    }
    setPickerOpen((v) => !v);
  };
  const applyTemplate = (key) => {
    api.post(`/events/${anchor.type}/${anchor.id}/readiness/checks`, { template: key })
      .then(() => { toast.success("Checklist added. Edit it to fit this event."); setPickerOpen(false); onChanged(); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  const addCheck = () => {
    if (!newCheck.trim()) return;
    api.post(`/events/${anchor.type}/${anchor.id}/readiness/checks`, { title: newCheck })
      .then(() => { setNewCheck(""); onChanged(); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  const toggle = (c) => api.patch(`/readiness/checks/${c.id}`, { done: !c.done }).then(onChanged).catch(() => {});
  const removeCheck = (c) => api.delete(`/readiness/checks/${c.id}`).then(onChanged).catch(() => {});
  if (finished && checks.length === 0) return null;
  return (
    <div className="mt-3 border-t border-[#F4F6F2] pt-3" data-testid="ws-readiness-checklist">
      {checks.map((c) => (
        <div key={c.id} className="group flex items-center gap-2 py-1" data-testid={`ws-check-${c.id}`}>
          <button data-testid={`ws-check-toggle-${c.id}`} onClick={() => toggle(c)}>
            {c.done ? <CheckCircle2 className="h-4 w-4 text-[#0F766E]" /> : <Circle className="h-4 w-4 text-[#CBD5E1]" />}
          </button>
          <span className={`min-w-0 flex-1 truncate text-xs ${c.done ? "text-[#9aa3a0] line-through" : "text-[#2D3436]"}`}>{c.title}</span>
          <button data-testid={`ws-check-remove-${c.id}`} onClick={() => removeCheck(c)}
            className="opacity-0 transition-opacity group-hover:opacity-100"><X className="h-3 w-3 text-[#CBD5E1] hover:text-[#B91C1C]" /></button>
        </div>
      ))}
      <div className="mt-1 flex gap-1.5">
        {!finished && (<>
          <input data-testid="ws-check-input" value={newCheck} onChange={(e) => setNewCheck(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCheck()} placeholder="Add a check…"
            className="min-w-0 flex-1 rounded-lg border border-[#E2E8F0] px-2.5 py-1.5 text-[11px] focus:border-[#0F766E] focus:outline-none" />
          <button data-testid="ws-check-add" onClick={addCheck} className="rounded-lg bg-[#0F766E] px-2.5 text-white"><Plus className="h-3 w-3" /></button>
        </>)}
      </div>
      {checks.length === 0 && !finished && (
        <button data-testid="ws-use-template" onClick={openPicker}
          className="mt-2 w-full rounded-xl border border-dashed border-[#0F766E]/40 py-1.5 text-[11px] font-medium text-[#0F766E] hover:bg-[#0F766E]/5">
          Use a readiness template
        </button>
      )}
      {pickerOpen && (
        <div className="mt-2 space-y-1" data-testid="ws-template-picker">
          {(templates || []).map((t) => (
            <button key={t.key} data-testid={`ws-template-${t.key}`} onClick={() => applyTemplate(t.key)}
              className="flex w-full items-center justify-between rounded-xl border border-[#EBECE8] px-3 py-2 text-left text-xs text-[#2D3436] hover:border-[#0F766E]/40">
              <span>{t.label}</span>
              <span className="text-[10px] text-[#9aa3a0]">{t.items.length} checks</span>
            </button>
          ))}
          {templates && templates.length === 0 && <p className="text-[11px] text-[#9aa3a0]">No templates for this record type.</p>}
        </div>
      )}
    </div>
  );
}

// Connected Event Workspace. anchor = { type, id } or null.
export default function EventWorkspace({ anchor, onClose }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [attach, setAttach] = useState(null);
  const [showReadyList, setShowReadyList] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [notePrivate, setNotePrivate] = useState(false);
  const load = () => {
    if (!anchor) return;
    api.get(`/events/${anchor.type}/${anchor.id}/workspace`)
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Could not open that event."); onClose(); });
  };
  useEffect(() => { setData(null); setTab("overview"); setAttach(null);
    load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [anchor?.type, anchor?.id]);
  if (!anchor) return null;

  const openSource = (link) => { onClose(); navigate(link); };
  const deleteNote = (id) => api.delete(`/events/notes/${id}`).then(load).catch(() => {});
  const addNote = () => {
    if (!noteText.trim()) return;
    api.post(`/events/${anchor.type}/${anchor.id}/notes`, { text: noteText, private: notePrivate })
      .then(() => { setNoteText(""); load(); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  const openAttach = (presetType = null, presetCreate = null) => setAttach({ presetType, presetCreate });

  const ev = data?.event;
  const ready = data?.readiness;
  const sections = data?.sections || {};
  const connected = Object.entries(CONNECT_META)
    .map(([key, meta]) => ({ key, meta, entries: sections[key] || [] }))
    .filter((c) => c.entries.length > 0);
  const prepEntries = sections.preparation || [];
  const budgetEntries = sections.budget || [];
  const peopleEntries = sections.people || [];
  const planned = data?.summary?.planned_amount || 0;
  const paid = data?.summary?.paid_amount || 0;
  const time = ev && fmtTime(ev.primary_date);

  const itemsEntries = ["documents", "lists", "reminders", "related_records"]
    .flatMap((k) => (sections[k] || []).map((e) => ({ ...e, _section: k })));

  const sectionBlock = (entries, emptyText) => (
    <div className="space-y-1.5">
      {entries.map((e) => <LinkedCard key={e.link.id} entry={e} onRemoved={load} onOpen={openSource} />)}
      {entries.length === 0 && (
        <p className="rounded-2xl border border-dashed border-[#E2E8F0] px-4 py-6 text-center text-xs text-[#9aa3a0]">{emptyText}</p>
      )}
    </div>
  );

  return (
    <div className={`fixed inset-0 z-50 flex ${isMobile ? "items-end" : "items-center justify-center p-3 sm:p-5"}`}
      data-testid="event-workspace" role="dialog" aria-modal="true" aria-label={ev ? ev.title : "Event workspace"}>
      <div className="absolute inset-0 bg-[#2D3436]/45 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative z-10 flex w-full flex-col overflow-hidden bg-white shadow-2xl ${isMobile
        ? "max-h-[94vh] rounded-t-3xl" : "max-h-[92vh] max-w-[1240px] rounded-3xl"}`}>
        {isMobile && <div className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-[#E2E8F0]" />}

        {/* Header: title, readiness score, meta, actions */}
        <div className="border-b border-[#EBECE8] px-5 pb-0 pt-4 sm:px-7">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl" style={{ background: "rgba(15,118,110,0.08)" }}>
              <Paperclip className="h-4.5 w-4.5 text-[#0F766E]" style={{ height: 18, width: 18 }} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="truncate font-display text-xl font-semibold text-[#2D3436]" data-testid="ws-title">
                  {ev ? ev.title : "Loading…"}
                </h2>
                {ready && <ReadinessPill ready={ready} />}
              </div>
              {ev && (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#636E72]">
                  {ev.primary_date && (
                    <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-[#9aa3a0]" /> {fmtDate(ev.primary_date)}</span>
                  )}
                  {time && <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-[#9aa3a0]" /> {time}</span>}
                  {ev.subtitle && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-[#9aa3a0]" /> {ev.subtitle}</span>}
                  <span className="rounded-full bg-[#F4F6F2] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#636E72]">
                    {HUB_LABELS[ev.hub] || ev.hub}
                  </span>
                  {ev.imported_from && (
                    <span data-testid="ws-imported-badge" className="rounded-full bg-[#1D4ED8]/8 px-2 py-0.5 text-[10px] font-semibold text-[#1D4ED8]">
                      Imported from {ev.imported_from}
                    </span>
                  )}
                </div>
              )}
            </div>
            {ev?.external_url && (
              <a data-testid="ws-open-external" href={ev.external_url} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 rounded-xl border border-[#E2E8F0] px-3 py-1.5 text-xs font-medium text-[#636E72] hover:border-[#0F766E]/40 hover:text-[#0F766E]">
                <ExternalLink className="h-3.5 w-3.5" /> Open in {ev.imported_from || "source calendar"}
              </a>
            )}
            {ev?.deep_link && !isMobile && (
              <button data-testid="ws-open-source" onClick={() => openSource(ev.deep_link)}
                className="flex items-center gap-1.5 rounded-xl border border-[#E2E8F0] px-3 py-1.5 text-xs font-medium text-[#636E72] hover:border-[#0F766E]/40 hover:text-[#0F766E]">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            <button data-testid="ws-close" onClick={onClose} className="rounded-full bg-[#F4F6F2] p-1.5 text-[#636E72] hover:bg-[#EBECE8]">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Nav tabs + Add */}
          <div className="mt-3 flex items-center gap-1 overflow-x-auto pb-0">
            {NAV_TABS.map((t) => (
              <button key={t.key} data-testid={`ws-nav-${t.key}`} onClick={() => setTab(t.key)}
                className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition-colors ${tab === t.key
                  ? "border-[#0F766E] text-[#0F766E]" : "border-transparent text-[#636E72] hover:text-[#2D3436]"}`}>
                <t.Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
            {!isMobile && (
              <button data-testid="ws-add-related-btn" onClick={() => openAttach()}
                className="ml-auto flex shrink-0 items-center gap-1.5 rounded-xl bg-[#0F766E] px-3.5 py-1.5 text-xs font-medium text-white">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            )}
          </div>
        </div>

        {!data ? (
          <div className="flex h-56 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#9aa3a0]" /></div>
        ) : (
          <div className="flex min-h-0 flex-1 gap-6 overflow-y-auto px-5 py-5 sm:px-7 lg:flex-row">
            <div className="min-w-0 flex-1">
              {/* Already Connected chips */}
              {connected.length > 0 && (
                <div className="mb-5" data-testid="ws-already-connected">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">Already connected</p>
                  <div className="flex flex-wrap gap-2">
                    {connected.map((c) => (
                      <button key={c.key} data-testid={`ws-connected-${c.key}`} onClick={() => setTab(c.meta.tab)}
                        className="flex items-center gap-2 rounded-xl border border-[#EBECE8] bg-white px-3 py-2 text-xs font-medium text-[#2D3436] transition-colors hover:border-[#0F766E]/40">
                        <c.meta.Icon className="h-3.5 w-3.5 text-[#0F766E]" />
                        {c.meta.label}
                        <span className="rounded-full bg-[#F4F6F2] px-1.5 py-0.5 text-[10px] font-semibold text-[#636E72]">
                          {c.key === "budget" && planned > 0 ? `\u00a3${planned.toFixed(0)}` : c.entries.length}
                        </span>
                        <ChevronRight className="h-3 w-3 text-[#CBD5E1]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {tab === "overview" && (
                <>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">Overview</p>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {/* Readiness card */}
                    <div className="rounded-2xl border border-[#EBECE8] bg-white p-4" data-testid="ws-readiness-card">
                      <p className="text-sm font-semibold text-[#2D3436]">Readiness</p>
                      <div className="mt-3 flex items-center gap-4">
                        <ReadinessRing ready={ready} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold" style={{ color: (READY_META[ready.state] || READY_META.not_assessed).color }}>
                            {(READY_META[ready.state] || READY_META.not_assessed).label}
                          </p>
                          <p className="mt-0.5 text-[11px] leading-relaxed text-[#636E72]">
                            {ready.state === "ready" ? "Everything required is in place."
                              : ready.state === "completed" ? "This event is complete."
                              : ready.state === "not_assessed" ? "Mark linked items as required to track readiness."
                              : `${ready.reasons.length} item${ready.reasons.length !== 1 ? "s" : ""} still need${ready.reasons.length === 1 ? "s" : ""} attention.`}
                          </p>
                        </div>
                      </div>
                      {ready.reasons?.length > 0 && (
                        <div className="mt-3 space-y-1" data-testid="ws-needs-attention">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#B45309]">Missing</p>
                          {ready.reasons.map((r, i) => (
                            <p key={i} className="flex items-start gap-1.5 text-[11px] text-[#7C4A03]">
                              <Circle className="mt-0.5 h-2 w-2 shrink-0 fill-[#F59E0B] text-[#F59E0B]" /> {r.text}
                            </p>
                          ))}
                        </div>
                      )}
                      {ready.satisfied?.length > 0 && (
                        <button data-testid="ws-view-ready" onClick={() => setShowReadyList((v) => !v)}
                          className="mt-3 w-full rounded-xl border border-[#E2E8F0] py-1.5 text-[11px] font-medium text-[#636E72] hover:border-[#0F766E]/40 hover:text-[#0F766E]">
                          {showReadyList ? "Hide what's ready" : "View what's ready"}
                        </button>
                      )}
                      {showReadyList && ready.satisfied?.map((s, i) => (
                        <p key={i} className="mt-1.5 flex items-start gap-1.5 text-[11px] text-[#0F766E]">
                          <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0" /> {s.text}
                        </p>
                      ))}
                      {ready.override && (
                        <p className="mt-2 text-[10px] text-[#9aa3a0]">Set manually{ready.override.by ? ` by ${ready.override.by}` : ""}{ready.override.reason ? `: ${ready.override.reason}` : ""}.</p>
                      )}
                      <ReadinessChecklist anchor={anchor} ev={ev} ready={ready} onChanged={load} />
                    </div>

                    {/* Preparation card */}
                    <div className="rounded-2xl border border-[#EBECE8] bg-white p-4" data-testid="ws-prep-card">
                      <p className="text-sm font-semibold text-[#2D3436]">Preparation</p>
                      <div className="mt-3 space-y-2">
                        {prepEntries.slice(0, 4).map((e) => {
                          const done = ["completed", "fulfilled"].includes(e.other.status);
                          return (
                            <button key={e.link.id} onClick={() => e.other.deep_link && openSource(e.other.deep_link)}
                              className="flex w-full items-center gap-2 text-left">
                              {done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[#0F766E]" />
                                : <Circle className="h-4 w-4 shrink-0 text-[#CBD5E1]" />}
                              <span className={`min-w-0 flex-1 truncate text-xs ${done ? "text-[#9aa3a0] line-through" : "text-[#2D3436]"}`}>{e.other.title}</span>
                              {e.link.required_for_readiness && !done && (
                                <span className="text-[10px] font-semibold text-[#B91C1C]">Missing</span>
                              )}
                            </button>
                          );
                        })}
                        {prepEntries.length === 0 && <p className="text-[11px] text-[#9aa3a0]">No preparation tasks yet.</p>}
                      </div>
                      <div className="mt-3 flex gap-3 border-t border-[#F4F6F2] pt-2.5 text-[11px] font-medium">
                        <button onClick={() => setTab("preparation")} className="text-[#636E72] hover:text-[#0F766E] hover:underline">View all tasks</button>
                        <button data-testid="ws-new-task" onClick={() => openAttach("home_life_item", "task")} className="text-[#0F766E] hover:underline">New task</button>
                      </div>
                    </div>

                    {/* Event details card */}
                    <div className="rounded-2xl border border-[#EBECE8] bg-white p-4 sm:col-span-2 xl:col-span-1" data-testid="ws-details-card">
                      <p className="text-sm font-semibold text-[#2D3436]">Event details</p>
                      <div className="mt-3 space-y-2 text-xs text-[#636E72]">
                        <p className="flex items-center gap-2"><Paperclip className="h-3.5 w-3.5 text-[#9aa3a0]" /> {ev.title}</p>
                        {ev.primary_date && <p className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5 text-[#9aa3a0]" /> {fmtDate(ev.primary_date)}{time ? ` \u00b7 ${time}` : ""}</p>}
                        <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-[#9aa3a0]" /> {ev.subtitle || HUB_LABELS[ev.hub] || ev.hub}</p>
                        <p className="flex items-center gap-2"><Bell className="h-3.5 w-3.5 text-[#9aa3a0]" /> {(sections.reminders || []).length ? `${sections.reminders.length} linked reminder${sections.reminders.length !== 1 ? "s" : ""}` : "No linked reminders"}</p>
                      </div>
                      {ev.deep_link && (
                        <button onClick={() => openSource(ev.deep_link)}
                          className="mt-3 flex items-center gap-1.5 rounded-xl border border-[#E2E8F0] px-3 py-1.5 text-[11px] font-medium text-[#636E72] hover:border-[#0F766E]/40 hover:text-[#0F766E]">
                          <ExternalLink className="h-3 w-3" /> View full details
                        </button>
                      )}
                      {ev.external_url && (
                        <a data-testid="ws-details-open-external" href={ev.external_url} target="_blank" rel="noreferrer"
                          className="mt-3 flex w-fit items-center gap-1.5 rounded-xl border border-[#E2E8F0] px-3 py-1.5 text-[11px] font-medium text-[#636E72] hover:border-[#0F766E]/40 hover:text-[#0F766E]">
                          <ExternalLink className="h-3 w-3" /> Open in {ev.imported_from || "source calendar"}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Related items category cards */}
                  <p className="mb-2 mt-6 text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">Related items</p>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="ws-related-grid">
                    <CategoryCard meta={CONNECT_META.documents} entries={sections.documents || []}
                      onAttach={() => openAttach("document")} onCreate={() => openAttach("document", "upload")}
                      createLabel="Upload new" onOpen={openSource} />
                    <CategoryCard meta={CONNECT_META.lists} entries={sections.lists || []}
                      onAttach={() => openAttach("shopping_list")} onCreate={() => openAttach("shopping_list", "list")}
                      createLabel="Create new" onOpen={openSource} />
                    <CategoryCard meta={CONNECT_META.budget} entries={budgetEntries} planned={planned > 0 ? planned : null}
                      onAttach={() => openAttach("budget_txn")} onCreate={() => openAttach("budget_txn", "spend")}
                      createLabel="New planned spend" onOpen={openSource} />
                    <CategoryCard meta={CONNECT_META.preparation} entries={prepEntries}
                      onAttach={() => openAttach("home_life_item")} onCreate={() => openAttach("home_life_item", "task")}
                      createLabel="New task" onOpen={openSource} />
                    <CategoryCard meta={CONNECT_META.people} entries={peopleEntries}
                      onAttach={() => openAttach("contact")} onOpen={openSource} />
                    <button data-testid="ws-add-something" onClick={() => openAttach()}
                      className="flex min-h-[130px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[#CBD5E1] text-[#636E72] transition-colors hover:border-[#0F766E]/50 hover:text-[#0F766E]">
                      <Plus className="h-5 w-5" />
                      <span className="text-xs font-medium">Add something</span>
                      <span className="px-4 text-center text-[10px] text-[#9aa3a0]">Attach or create a new item for this event.</span>
                    </button>
                  </div>
                </>
              )}

              {tab === "preparation" && (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">Preparation</p>
                    <button data-testid="ws-prep-new" onClick={() => openAttach("home_life_item", "task")}
                      className="flex items-center gap-1 text-xs font-medium text-[#0F766E]"><Plus className="h-3.5 w-3.5" /> New task</button>
                  </div>
                  {sectionBlock(prepEntries, "No preparation tasks yet. Create one so nothing gets forgotten.")}
                </>
              )}

              {tab === "items" && (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">Related items</p>
                    <button onClick={() => openAttach()} className="flex items-center gap-1 text-xs font-medium text-[#0F766E]"><Plus className="h-3.5 w-3.5" /> Add</button>
                  </div>
                  {sectionBlock(itemsEntries, "Nothing linked yet. Attach documents, lists, reminders or Hub records.")}
                </>
              )}

              {tab === "budget" && (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">Budget & payments</p>
                    <button data-testid="ws-budget-new" onClick={() => openAttach("budget_txn", "spend")}
                      className="flex items-center gap-1 text-xs font-medium text-[#0F766E]"><Plus className="h-3.5 w-3.5" /> New planned spend</button>
                  </div>
                  {(planned > 0 || paid > 0) && (
                    <div className="mb-3 flex gap-4 rounded-2xl border border-[#EBECE8] bg-[#FAFBF9] px-4 py-2.5 text-xs" data-testid="ws-budget-summary">
                      <span className="text-[#636E72]">Planned <strong className="text-[#2D3436]">{"\u00a3"}{planned.toFixed(2)}</strong></span>
                      <span className="text-[#636E72]">Recorded <strong className="text-[#2D3436]">{"\u00a3"}{paid.toFixed(2)}</strong></span>
                    </div>
                  )}
                  {sectionBlock(budgetEntries, "No Budget links yet. Plan spending for this event or attach a payment.")}
                </>
              )}

              {tab === "people" && (
                <>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">People & contacts</p>
                    <button onClick={() => openAttach("contact")} className="flex items-center gap-1 text-xs font-medium text-[#0F766E]"><Plus className="h-3.5 w-3.5" /> Add person</button>
                  </div>
                  {sectionBlock(peopleEntries, "No people linked yet. Attach the household members involved.")}
                </>
              )}

              {tab === "activity" && (
                <div data-testid="ws-activity">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">Notes</p>
                  {data.notes.map((n) => (
                    <div key={n.id} className="mb-1.5 flex items-start gap-2 rounded-2xl border border-[#EBECE8] bg-white px-3.5 py-2.5">
                      <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#9aa3a0]" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs text-[#2D3436]">{n.text}</span>
                        <span className="block text-[10px] text-[#9aa3a0]">{n.created_by_name}{n.private ? " \u00b7 private" : ""}</span>
                      </span>
                      <button onClick={() => deleteNote(n.id)}><Trash2 className="h-3 w-3 text-[#CBD5E1] hover:text-[#B91C1C]" /></button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <input data-testid="ws-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add a note…"
                      className="flex-1 rounded-xl border border-[#E2E8F0] px-3 py-2 text-xs focus:border-[#0F766E] focus:outline-none" />
                    <button data-testid="ws-note-add" onClick={addNote} className="rounded-xl bg-[#0F766E] px-3 text-white"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <label className="mt-1.5 flex items-center gap-1.5 text-[10px] text-[#9aa3a0]">
                    <input type="checkbox" checked={notePrivate} onChange={(e) => setNotePrivate(e.target.checked)} /> Private to me
                  </label>
                  <p className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">Activity</p>
                  {data.activity.length === 0 && <p className="text-[11px] text-[#9aa3a0]">No activity yet.</p>}
                  {data.activity.map((a) => (
                    <p key={a.id} className="py-0.5 text-[11px] text-[#9aa3a0]">
                      {a.text} {"\u00b7"} {new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Live summary sidebar (desktop) */}
            {!isMobile && (
              <div className="hidden w-56 shrink-0 lg:block" data-testid="ws-sidebar">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#9aa3a0]">This event contains</p>
                <div className="space-y-1.5" data-testid="ws-contains">
                  {connected.length === 0 && <p className="text-[11px] text-[#9aa3a0]">Nothing linked yet.</p>}
                  {connected.map((c) => (
                    <button key={c.key} onClick={() => setTab(c.meta.tab)} className="flex w-full items-center gap-2 text-left text-xs text-[#636E72] hover:text-[#0F766E]">
                      <c.meta.Icon className="h-3.5 w-3.5 text-[#0F766E]" />
                      {c.key === "budget" && planned > 0
                        ? `\u00a3${planned.toFixed(0)} planned`
                        : `${c.entries.length} ${c.entries.length === 1 ? c.meta.label.replace(/s$/, "") : c.meta.label}`}
                    </button>
                  ))}
                  {data.notes.length > 0 && (
                    <button onClick={() => setTab("activity")} className="flex w-full items-center gap-2 text-left text-xs text-[#636E72] hover:text-[#0F766E]">
                      <StickyNote className="h-3.5 w-3.5 text-[#0F766E]" /> {data.notes.length} Note{data.notes.length !== 1 ? "s" : ""}
                    </button>
                  )}
                </div>
                <div className="mt-5 rounded-2xl border border-[#F59E0B]/25 bg-[#FFFBEB] p-3.5">
                  <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-[#B45309]">
                    <Lightbulb className="h-3.5 w-3.5" /> Tip
                  </p>
                  <p className="text-[11px] leading-relaxed text-[#7C4A03]">
                    Everything linked here is connected to this event and easy to find.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mobile floating Add */}
        {isMobile && data && (
          <button data-testid="ws-add-related-btn" onClick={() => openAttach()}
            className="absolute bottom-5 right-5 flex items-center gap-1.5 rounded-full bg-[#0F766E] px-4 py-2.5 text-sm font-medium text-white shadow-lg">
            <Plus className="h-4 w-4" /> Add
          </button>
        )}
      </div>

      {attach && (
        <AttachSheet anchor={anchor} presetType={attach.presetType} presetCreate={attach.presetCreate}
          isMobile={isMobile} onClose={() => setAttach(null)}
          onDone={() => { setAttach(null); load(); }} />
      )}
    </div>
  );
}

// Map a unified /calendar/events item to its workspace anchor. Null == no source anchor.
export function workspaceAnchor(ev) {
  if (!ev) return null;
  if (ev.source === "reminders") return { type: "reminder", id: ev.id };
  if (ev.source === "scheduled" && ev.meta?.scheduled_object_id)
    return { type: "scheduled_object", id: ev.meta.scheduled_object_id };
  if (ev.source === "flights") {
    const m = /\/app\/travel\/(.+)$/.exec(ev.link || "");
    return m ? { type: "trip", id: m[1] } : null;
  }
  if (ev.source === "external" && ev.meta?.external_event_id)
    return { type: "external_event", id: ev.meta.external_event_id };
  if (ev.source === "home_life") {
    if (ev.meta?.series) return { type: "home_life_item", id: ev.meta.series };
    const id = String(ev.id);
    if (id.startsWith("hlmeal-")) return { type: "home_life_meal", id: id.slice(7) };
    if (id.startsWith("hlbin-")) return { type: "home_life_bin_schedule", id: id.slice(6, -11) };
  }
  return null;
}
